import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DATE_FORMAT } from 'src/app/config/global.config';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import {
  IPriceCandidate,
  IPriceCandidateRow,
  IPricedLine,
  IPricePolicyTrace,
  ISettingsPreferredLine,
  MAX_VISIBLE_CANDIDATES,
  bestPerSource,
  candidateApplyPayload,
  policyExplanation,
  settingsPreferredLine,
  toPriceCandidateRows,
  PriceSourceType,
} from '../../models/quotations.models';
import { PriceSourcePreviewService } from 'src/app/shared/file-preview-dialog/price-source-preview.service';

@Component({
  selector: 'app-price-details-tooltip',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule, SvgIconComponent],
  templateUrl: './price-details-tooltip.component.html',
  styleUrls: ['./price-details-tooltip.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PriceDetailsTooltipComponent implements OnChanges {
  constructor(
    private translateService: TranslateService,
    private sourcePreview: PriceSourcePreviewService,
  ) {}

  /**
   * The line whose price is being explained — asked for by its price fields
   * alone (`IPricedLine`), because the two screens that show this tooltip hold
   * two unrelated line models.
   */
  @Input() product!: IPricedLine;
  @Input() currencySymbol: string = '';
  @Input() viewMode: boolean = false;
  /**
   * The order's no-price lock, passed as itself instead of folded into
   * `viewMode`. Both hide the apply buttons, but for opposite reasons:
   * `viewMode` is "you are reading", the lock is "the server will refuse", and
   * only the second has something to explain on screen.
   */
  @Input() noPriceLocked: boolean = false;
  /**
   * Every price the engine considered for this product, ordered. Empty (the
   * default) leaves the three fixed rows untouched and the candidates
   * section absent.
   */
  @Input() candidates: IPriceCandidate[] = [];
  /** Why this price and not another — one readable line from the engine. */
  @Input() policyTrace: IPricePolicyTrace | null = null;
  /** What the engine would apply, so "why this price" can name it. */
  @Input() selectedCandidate: IPriceCandidate | null = null;
  @Output() applyPrice = new EventEmitter<{ price: number; type: PriceSourceType }>();
  /** Asked when the operator clicks "see the N other known prices". */
  @Output() seeAllCandidates = new EventEmitter<void>();

  /** The app's one date format. An ISO string on screen is a leak, not a date. */
  readonly DATE_FORMAT = DATE_FORMAT;

  get currentPriceType(): PriceSourceType {
    return this.product?.price_type ?? 'catalog';
  }

  /** The i18n key of the badge, for every one of the six price types. */
  get currentPriceSourceKey(): string {
    return `quotations_and_orders.price_source_${this.currentPriceType}`;
  }

  /** Read-only, whichever of the two reasons put it there. */
  get isReadOnly(): boolean {
    return this.viewMode || this.noPriceLocked;
  }

  get isSystemApplied(): boolean {
    return this.currentPriceType === 'catalog';
  }

  get isAiApplied(): boolean {
    return this.currentPriceType === 'ai';
  }

  get isOverrideApplied(): boolean {
    return this.currentPriceType === 'override';
  }

  get isNoneApplied(): boolean {
    return this.currentPriceType === 'none';
  }

  /**
   * What the AI actually sent. When the sub-unit conversion fired, `ai_price`
   * is already re-expressed per selling unit (e.g. per pack), not what the AI
   * sent (e.g. per piece) — so this falls back to the true pre-conversion
   * snapshot (`ai_extracted_price`) instead. Applying the price still always
   * uses `ai_price` (the correct, converted, actionable value).
   */
  get displayedParsedPrice(): number | null | undefined {
    return this.product?.ai_unit_conversion_applied
      ? this.product.ai_extracted_price
      : this.product?.ai_price;
  }

  /** Shows a small note explaining why the parsed price differs from the applied price. */
  get showConversionNote(): boolean {
    return !!this.product?.ai_unit_conversion_applied;
  }

  onApplySystemPrice(event: Event): void {
    event.stopPropagation();
    if (this.product.catalog_price != null) {
      this.applyPrice.emit({ price: this.product.catalog_price, type: 'catalog' });
    }
  }

  onApplyAiPrice(event: Event): void {
    event.stopPropagation();
    if (this.product.ai_price != null) {
      this.applyPrice.emit({ price: this.product.ai_price, type: 'ai' });
    }
  }

  onApplyNoPrice(event: Event): void {
    event.stopPropagation();
    this.applyPrice.emit({ price: 0, type: 'none' });
  }

  formatPrice(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) return '--';
    return Number(value).toFixed(2) + ' ' + this.currencySymbol;
  }

  // ───────────────────────────────────────────────────────────────────
  // "Other known prices"
  // ───────────────────────────────────────────────────────────────────

  /**
   * The candidates the compact tooltip shows, resolved once per input change.
   *
   * Expired ones are hidden as long as a live one remains: an operator does not
   * need to be told a price lapsed when a current one answers the question.
   * When everything has expired they are shown, struck through, because "we
   * only have a lapsed price" is itself the answer.
   *
   * The candidate the line is currently priced from is re-injected when the
   * slice dropped it: the tooltip explains the number in front of the operator,
   * so hiding its source would defeat the point.
   */
  visibleRows: IPriceCandidateRow[] = [];
  /** How many candidates the compact view does not show. Never negative. */
  hiddenCandidateCount = 0;

  ngOnChanges(): void {
    const all = this.candidates ?? [];
    const live = all.filter((c) => c.freshness !== 'expired');
    const pool = live.length ? live : all;
    const shown = bestPerSource(pool).slice(0, MAX_VISIBLE_CANDIDATES);

    // Rows are built from all the candidates, not from the pool: the row the
    // line is currently priced from must never be dropped, and it can be an
    // expired candidate absent from `pool`. Passing the line's own price marks
    // the row it was written from as current, so the re-injection below picks
    // that one row.
    const rows = toPriceCandidateRows(
      all,
      this.currencySymbol,
      this.currentPriceType,
      this.isNoneApplied ? null : this.product?.final_price
    );
    const shownCandidates = new Set(shown);
    this.visibleRows = rows.filter((row) => shownCandidates.has(row.candidate));
    const current = rows.find((row) => row.current);
    if (current && !this.visibleRows.includes(current)) {
      this.visibleRows = [...this.visibleRows, current];
    }
    this.hiddenCandidateCount = Math.max(0, all.length - this.visibleRows.length);
    this.why = policyExplanation(this.policyTrace, this.selectedCandidate, (key) =>
      this.translateService.instant(key)
    );
    this.settingsPreferred = settingsPreferredLine(
      this.selectedCandidate,
      this.isNoneApplied ? null : this.product?.final_price,
      this.currencySymbol
    );
  }

  /** The translated "why this price" sentence, or null when there is none. */
  why: { key: string; params: Record<string, string> } | null = null;

  /**
   * "According to your settings: Imported tariff 6.80 €", present only when the
   * engine would put another number on the line — the guard rail of the direct
   * capture. Applying it covers this line alone.
   */
  settingsPreferred: ISettingsPreferredLine | null = null;

  onApplySettingsPreferred(event: Event): void {
    event.stopPropagation();
    if (!this.settingsPreferred) return;
    const payload = candidateApplyPayload(this.settingsPreferred.candidate);
    if (payload) this.applyPrice.emit(payload);
  }

  /** The link is absent, not disabled, when there is nothing more to see. */
  get showSeeAllLink(): boolean {
    return this.hiddenCandidateCount > 0;
  }

  /** Present only when the section has something to show. */
  get showCandidatesSection(): boolean {
    return this.visibleRows.length > 0;
  }

  trackByRow(_index: number, row: IPriceCandidateRow): string {
    return row.key;
  }

  onApplyCandidate(event: Event, row: IPriceCandidateRow): void {
    event.stopPropagation();
    const payload = candidateApplyPayload(row.candidate);
    if (payload) this.applyPrice.emit(payload);
  }

  onSeeAll(event: Event): void {
    event.stopPropagation();
    this.seeAllCandidates.emit();
  }

  /**
   * Show the document this price was read from.
   *
   * Handled here, not emitted: the tooltip has three hosts, each would forward
   * the same payload to the same root service, and one that forgets the binding
   * leaves a dead button.
   */
  onPreviewSource(event: Event, row: IPriceCandidateRow): void {
    event.stopPropagation();
    if (!row.documentId) return;
    this.sourcePreview.open({ documentId: row.documentId, label: row.provenance });
  }

}
